import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useAuthedApi, errMsg } from "../lib/api";
import BackButton from "./BackButton";

const Txt = ({ label, v, set, max, id, area }) => (
  <div><label className="lux-label">{label}</label>
    {area ? <textarea className="lux-input min-h-[70px]" maxLength={max} value={v} onChange={(e) => set(e.target.value)} data-testid={id} />
      : <input className="lux-input" maxLength={max} value={v} onChange={(e) => set(e.target.value)} data-testid={id} />}
  </div>
);

export default function AdvertiseEditor({ value, onClose, onSaved }) {
  const api = useAuthedApi();
  const [f, setF] = useState(value);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const setPerk = (i, k, v) => setF((p) => ({ ...p, perks: p.perks.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));

  const save = async () => {
    if (f.perks.some((p) => !p.title.trim())) return toast.error("Every ad option needs a title");
    setBusy(true);
    try { const r = await api("put", "/admin/settings/advertise", f); onSaved?.(r); toast.success("Advertise page updated"); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111319] border-[#D4AF37]/30 text-white sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="advertise-editor">
        <DialogHeader><DialogTitle className="font-display text-3xl gold-text">Edit Advertise page</DialogTitle></DialogHeader>
        <div><BackButton onClick={onClose} testid="advertise-editor-back" /></div>
        <div className="grid gap-3">
          <Txt label="Heading" v={f.heading} set={set("heading")} max={80} id="ad-heading" />
          <Txt label="Tagline" v={f.tagline} set={set("tagline")} max={120} id="ad-tagline" />
          <Txt label="Intro text" v={f.intro} set={set("intro")} max={500} id="ad-intro" area />
          <div className="grid sm:grid-cols-2 gap-3">
            <Txt label="WhatsApp pre-filled message" v={f.wa_text} set={set("wa_text")} max={200} id="ad-wa-text" />
            <Txt label="Note under WhatsApp button" v={f.wa_note} set={set("wa_note")} max={160} id="ad-wa-note" />
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between"><p className="eyebrow !text-[10px]">Ad options & prices</p>
            <button type="button" disabled={f.perks.length >= 12} onClick={() => set("perks")([...f.perks, { title: "", desc: "", price: "" }])} className="btn-outline !py-1 !px-3 text-xs" data-testid="ad-perk-add"><Plus className="w-3.5 h-3.5" />Add option</button></div>
          {f.perks.map((p, i) => (
            <div key={i} className="lux-card p-3 grid sm:grid-cols-[1fr_1fr_120px_auto] gap-2 items-end" data-testid={`ad-perk-${i}`}>
              <Txt label="Title" v={p.title} set={(v) => setPerk(i, "title", v)} max={60} id={`ad-perk-title-${i}`} />
              <Txt label="Description" v={p.desc} set={(v) => setPerk(i, "desc", v)} max={200} id={`ad-perk-desc-${i}`} />
              <Txt label="Price (optional)" v={p.price} set={(v) => setPerk(i, "price", v)} max={40} id={`ad-perk-price-${i}`} />
              <button type="button" onClick={() => set("perks")(f.perks.filter((_, j) => j !== i))} className="w-9 h-9 rounded-full border border-white/10 hover:border-red-400 text-red-400 flex items-center justify-center" aria-label="Remove" data-testid={`ad-perk-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <Txt label="Perfect for (one per line)" v={f.who.join("\n")} set={(v) => set("who")(v.split("\n"))} max={1200} id="ad-who" area />
        <div className="flex justify-end"><button className="btn-gold" disabled={busy} onClick={save} data-testid="ad-save">{busy ? "Saving..." : "Save changes"}</button></div>
      </DialogContent>
    </Dialog>
  );
}
